/*
 * @Description: 国际化配置文件
 * @Author: Mars
 * @Date: 2023-12-01 09:55:17
 */
import i18n, { BackendModule, ReadCallback, ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';
import config, { isDev } from '@/config';
import { applyUrlCountry, isSupportedLanguage } from '@/utils/countryUrlParams';

// RES-3272 分享链接：URL 国家/语言参数须在读取本地语言之前落地（纯本地操作，不发请求）
applyUrlCountry();

/**
 * 本地翻译资源，随项目一起打包，不再从远程翻译服务拉取。
 * 文件布局沿用远程的 `locales/{ns}/{lng}.json`，翻译平台重新导出后可直接覆盖。
 * 这里用非 eager 的 glob：Vite 为每个 JSON 生成动态 import，
 * 再由 vite.config.ts 的 manualChunks 按语言合并成一个 chunk，首屏只加载当前语言。
 */
const localeModules = import.meta.glob<{ default: ResourceKey }>('./locales/*/*.json');

/**
 * 语言 code → 翻译文件名的别名。
 *
 * 西班牙语的 code 是 `sp-SP`，但它不是合规的 BCP 47 标签（ISO 639-1 没有 `sp`、
 * ISO 3166-1 alpha-2 里西班牙是 `ES` 而 `SP` 未分配）。这个 code 暂时不能改：
 * 它是 config.languageList 的生效值，也被 utils/request.ts 原样作为 `language`
 * 请求头、utils/alphaq.ts 作为 `Accept-Language` 发给后端与 AlphaQ，
 * 前端单方面改会与后端契约脱节。
 *
 * 所以这里让 code 与文件名解耦：翻译文件按规范命名 `es-ES`（与翻译平台导出、
 * 与 antelope-i18n 的 languages.json 一致，重新导出可直接覆盖不会又多出一份），
 * 运行时通过本表映射。等后端接受 `es-ES` 后，删掉这条别名并把 languageList
 * 改成 `es-ES` 即可，翻译文件一个都不用动。
 *
 * 同一套做法 hooks/useAntdLocale 也在用：把 sp-SP 映射到 antd 的 es_ES 语言包。
 */
const LOCALE_FILE_ALIAS: Record<string, string> = { 'sp-SP': 'es-ES' };

/**
 * 运行时语言 code → 合规的 BCP 47 标签。目前只有西语一处差异（`sp-SP` → `es-ES`），给需要把语言交给标准 API /
 * 第三方的地方用（Intl.*、AlphaQ transcribe 的 locale）；发给后端的 `language` 头仍传原 code，不经这里。
 */
export const toBcp47Language = (code: string): string => LOCALE_FILE_ALIAS[code] ?? code;

/** 替代 i18next-http-backend：从本地打包的 JSON 读取翻译 */
const localResourcesBackend: BackendModule = {
  type: 'backend',
  /** 本地资源不需要初始化 */
  init: () => undefined,
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const file = LOCALE_FILE_ALIAS[language] ?? language;
    const loadModule = localeModules[`./locales/${namespace}/${file}.json`];
    // 该语言缺少此命名空间的文件时，与原先远程 404 的表现保持一致：
    // 不报错，由 i18next 回落到直接显示 key
    if (!loadModule) {
      callback(null, {});
      return;
    }
    loadModule().then(
      (module) => callback(null, module.default),
      (error: Error) => callback(error, false),
    );
  },
};

i18n
  // .use(LanguageDetector)
  .use(initReactI18next)
  .use(localResourcesBackend);

let booted = false;

/**
 * 用确定的语言启动 i18n（幂等，已启动时只切语言）。
 *
 * 不在模块加载时 init，是为了等大区列表校验出最终语言：早启动会先按暂定语言加载一份 locale
 * chunk，校正后再加载目标语言那份，用户还会看见界面语言闪一下。
 *
 * 启动必须早于 React 挂载：hooks/useLanguage、useAntdLocale 内部调
 * react-i18next 的 useTranslation，而 App 顶部就用了它们——i18n 未 init 时 useTranslation
 * 会提前 return、少调一个 useState，init 后 hook 数量对不上，React 抛 "Should have a queue"。
 * 编排见 utils/boot。
 */
export const bootI18n = (language: string) => {
  const lng = isSupportedLanguage(language) ? language : config.defaultLanguage;
  if (booted) return i18n.changeLanguage(lng);
  booted = true;
  return i18n.init({
    // 检测localStorage中的语言，如果不存在则使用默认语言en-US
    // detection: { lookupLocalStorage: 'language', order: ['localStorage'], caches: ['localStorage'] },
    lng,
    fallbackLng: false,
    // 翻译已按语言合并成单个 chunk，home / alphaq 与 common 同处一个文件，
    // 预加载省不掉任何请求，故只留 common。注意 i18next 会用 ns[0] 反推 defaultNS，
    // 这一项不能删（删掉 defaultNS 会变回 'translation'）。
    ns: ['common'],
    load: 'currentOnly',
    interpolation: {
      escapeValue: false,
      prefix: '{',
      suffix: '}',
    },
    react: {
      useSuspense: true,
    },
    debug: isDev,
  });
};

export default i18n;
